'use client';

import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { GridAddTimeRow } from '@/components/tournament/grid/GridAddTimeRow';
import { GridHiddenTimesNotice } from '@/components/tournament/grid/GridHiddenTimesNotice';
import { useMediaQuery } from '@/hooks/use-media-query';
import type { OrganizedMatch } from '@/types/organized-match.model';
import {
  matchStatus,
  matchStatusLabel,
  matchSummary,
  type MatchStatus,
  type WeekCell,
  type WeekGrid,
} from './turnos-model';

const STATUS_CLASSES: Record<MatchStatus, string> = {
  empty: 'bg-[#fef3c7] text-[#92400e]',
  missing: 'bg-[#dbeafe] text-[#1e40af]',
  full: 'bg-[#dcfce7] text-[#166534]',
};

const STATUSES: MatchStatus[] = ['empty', 'missing', 'full'];

export function TurnosLegend() {
  return (
    <div className="flex gap-3 text-[11px]">
      {STATUSES.map((status) => (
        <span key={status} className={`rounded px-2 py-0.5 ${STATUS_CLASSES[status]}`}>
          {matchStatusLabel(status)}
        </span>
      ))}
    </div>
  );
}

interface TurnosGridProps {
  grid: WeekGrid;
  /** Club-local "yyyy-MM-dd" of today, used to pick the initial day on small screens. */
  today: string;
  onCreate: (cell: WeekCell) => void;
  onOpen: (match: OrganizedMatch) => void;
  /** Adds a row ("HH:mm") the club schedule does not produce. */
  onAddTime: (time: string) => void;
  /** Takes a row out of the view; only offered while no match sits on it. */
  onRemoveTime: (time: string) => void;
  /** Brings back every row taken out of the view. */
  onShowHiddenTimes: () => void;
}

interface LayoutProps {
  grid: WeekGrid;
  onCreate: (cell: WeekCell) => void;
  onOpen: (match: OrganizedMatch) => void;
  onRemoveTime: (time: string) => void;
}

function TimeLabel({
  time,
  removable,
  onRemove,
}: {
  time: string;
  removable: boolean;
  onRemove: (time: string) => void;
}) {
  return (
    <div className="flex items-start justify-end gap-1 p-1.5 text-gray-500">
      {time}
      {removable ? (
        <button
          type="button"
          onClick={() => onRemove(time)}
          title="Sacar este horario de la grilla"
          aria-label={`Sacar el horario ${time}`}
          className="mt-px rounded text-gray-400 hover:text-red-600"
        >
          <X className="h-3 w-3" />
        </button>
      ) : (
        // Keeps every time aligned whether or not its row can be taken out.
        <span className="w-3" aria-hidden />
      )}
    </div>
  );
}

function Cell({
  cell,
  onCreate,
  onOpen,
}: {
  cell: WeekCell | undefined;
  onCreate: (cell: WeekCell) => void;
  onOpen: (match: OrganizedMatch) => void;
}) {
  if (!cell) return <div className="min-h-[34px] rounded bg-muted/40" />;
  return (
    <div
      data-testid={`turno-cell-${cell.date}T${cell.time}`}
      className="flex min-h-[34px] flex-col gap-1 rounded bg-muted/40 p-1"
    >
      {cell.matches.map((match) => (
        <button
          key={match.id}
          type="button"
          data-testid={`turno-card-${match.id}`}
          data-status={matchStatus(match)}
          onClick={() => onOpen(match)}
          className={`rounded px-1.5 py-1 text-left text-[11px] ${STATUS_CLASSES[matchStatus(match)]}`}
        >
          {matchSummary(match)}
        </button>
      ))}
      {cell.canCreate && (
        <button
          type="button"
          aria-label={`Crear turno ${cell.date} ${cell.time}`}
          data-testid={`turno-create-${cell.date}T${cell.time}`}
          onClick={() => onCreate(cell)}
          className="flex items-center justify-center rounded border border-dashed border-gray-300 bg-white py-1 text-gray-400 hover:border-blue-400 hover:text-blue-600"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function DesktopGrid({ grid, onCreate, onOpen, onRemoveTime }: LayoutProps) {
  return (
    <div className="overflow-x-auto">
      <div
        className="grid min-w-[720px] gap-0.5 text-xs"
        style={{ gridTemplateColumns: '64px repeat(7, minmax(0, 1fr))' }}
      >
        <div />
        {grid.days.map((day) => (
          <div key={day.date} className="p-1.5 font-semibold text-gray-600">
            {day.label}
          </div>
        ))}
        {grid.times.map((time) => (
          <div key={time} className="contents">
            <TimeLabel
              time={time}
              removable={!grid.occupiedTimes.has(time)}
              onRemove={onRemoveTime}
            />
            {grid.days.map((day) => (
              <Cell
                key={day.date}
                cell={grid.cells[`${day.date}T${time}`]}
                onCreate={onCreate}
                onOpen={onOpen}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function DayTabs({
  grid,
  activeDay,
  onDayChange,
  onCreate,
  onOpen,
  onRemoveTime,
}: LayoutProps & { activeDay: string; onDayChange: (day: string) => void }) {
  return (
    <Tabs value={activeDay} onValueChange={onDayChange}>
      <TabsList className="h-auto w-full flex-wrap justify-start">
        {grid.days.map((day) => (
          <TabsTrigger key={day.date} value={day.date}>
            {day.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {grid.days.map((day) => (
        <TabsContent key={day.date} value={day.date}>
          <div className="flex flex-col gap-1">
            {grid.times.map((time) => {
              const cell = grid.cells[`${day.date}T${time}`];
              if (!cell) return null;
              return (
                <div key={time} className="grid grid-cols-[64px_1fr] items-start gap-1 text-xs">
                  <TimeLabel
                    time={time}
                    removable={!grid.occupiedTimes.has(time)}
                    onRemove={onRemoveTime}
                  />
                  <Cell cell={cell} onCreate={onCreate} onOpen={onOpen} />
                </div>
              );
            })}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

export function TurnosGrid({
  grid,
  today,
  onCreate,
  onOpen,
  onAddTime,
  onRemoveTime,
  onShowHiddenTimes,
}: TurnosGridProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const defaultDay = grid.days.some((d) => d.date === today) ? today : grid.days[0].date;
  const [selectedDay, setSelectedDay] = useState(defaultDay);
  const activeDay = grid.days.some((d) => d.date === selectedDay) ? selectedDay : defaultDay;

  return (
    <div className="flex flex-col gap-2">
      <GridHiddenTimesNotice times={grid.hiddenTimes} onShow={onShowHiddenTimes} />
      {grid.times.length === 0 ? (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          El club no tiene horarios cargados para esta semana.
        </p>
      ) : (
        <>
          {isDesktop ? (
            <DesktopGrid
              grid={grid}
              onCreate={onCreate}
              onOpen={onOpen}
              onRemoveTime={onRemoveTime}
            />
          ) : (
            <DayTabs
              grid={grid}
              activeDay={activeDay}
              onDayChange={setSelectedDay}
              onCreate={onCreate}
              onOpen={onOpen}
              onRemoveTime={onRemoveTime}
            />
          )}
          <GridAddTimeRow onAdd={onAddTime} />
        </>
      )}
    </div>
  );
}
